import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  apiClient,
  type IdentifierDefinitionDto,
  type IdentifierDefinitionTypeCode,
} from '@/api/client';
import { queryKeys } from '@/query/keys';

/**
 * View model for an identifier definition. Wire DTO has every field optional;
 * the view model normalises to non-null booleans/strings so the UI doesn't
 * have to branch on undefined.
 */
export interface AdminIdentifierTypeViewModel {
  readonly id: string;
  readonly userEnteredId: string;
  readonly label: string;
  readonly type: IdentifierDefinitionTypeCode | null;
  readonly order: number | null;
  readonly prefix: string | null;
  readonly format: string | null;
  readonly formatHint: string | null;
  readonly enforceFormat: boolean;
  readonly allowDuplicates: boolean;
  readonly autoGenerate: boolean;
  readonly isPrimary: boolean;
  readonly active: boolean;
}

export function mapAdminIdentifierType(dto: IdentifierDefinitionDto): AdminIdentifierTypeViewModel {
  return {
    id: dto.id ?? '',
    userEnteredId: dto.userEnteredId ?? '',
    label: dto.label ?? '',
    type: dto.type ?? null,
    order: dto.order ?? null,
    prefix: dto.prefix ?? null,
    format: dto.format ?? null,
    formatHint: dto.formatHint ?? null,
    enforceFormat: dto.enforceFormat ?? false,
    allowDuplicates: dto.allowDuplicates ?? false,
    autoGenerate: dto.autoGenerate ?? false,
    isPrimary: dto.isPrimary ?? false,
    // BE column is NOT NULL with default true, so legacy rows always come back populated;
    // the `?? true` keeps the FE safe if the contract drifts.
    active: dto.active ?? true,
  };
}

export interface AdminIdentifierTypeInput {
  readonly userEnteredId: string;
  readonly label: string;
  readonly type: IdentifierDefinitionTypeCode | null;
  readonly order: number | null;
  readonly prefix: string | null;
  readonly format: string | null;
  readonly formatHint: string | null;
  readonly enforceFormat: boolean;
  readonly allowDuplicates: boolean;
  readonly autoGenerate: boolean;
  readonly isPrimary: boolean;
  readonly active: boolean;
}

export interface UpdateAdminIdentifierTypeInput extends AdminIdentifierTypeInput {
  readonly id: string;
}

function toDto(input: AdminIdentifierTypeInput, id?: string): IdentifierDefinitionDto {
  return {
    ...(id !== undefined ? { id } : {}),
    userEnteredId: input.userEnteredId,
    label: input.label,
    ...(input.type !== null ? { type: input.type } : {}),
    order: input.order,
    prefix: input.prefix,
    format: input.format,
    formatHint: input.formatHint,
    enforceFormat: input.enforceFormat,
    allowDuplicates: input.allowDuplicates,
    autoGenerate: input.autoGenerate,
    isPrimary: input.isPrimary,
    active: input.active,
  };
}

/**
 * Same query key as `useIdentifierTypes` so the create-patient dropdown stays
 * in sync with admin-side edits without a manual refresh.
 */
export const adminIdentifierTypesQueryKey = queryKeys.identifierTypes;

export function useAdminIdentifierTypes(): UseQueryResult<AdminIdentifierTypeViewModel[]> {
  return useQuery<AdminIdentifierTypeViewModel[]>({
    queryKey: adminIdentifierTypesQueryKey,
    queryFn: async ({ signal }) => {
      const page = await apiClient.getIdentifierDefinitions({ size: 200, signal });
      return (page.content ?? []).map(mapAdminIdentifierType);
    },
  });
}

type Snapshot = AdminIdentifierTypeViewModel[] | undefined;

/** Mirror the BE single-primary invariant inside an optimistic snapshot. */
function applyPrimaryUniqueness(
  rows: AdminIdentifierTypeViewModel[],
  promotedId: string,
): AdminIdentifierTypeViewModel[] {
  return rows.map((row) =>
    row.id === promotedId ? row : row.isPrimary ? { ...row, isPrimary: false } : row,
  );
}

export function useAdminIdentifierTypeMutations(): {
  readonly create: UseMutationResult<AdminIdentifierTypeViewModel, Error, AdminIdentifierTypeInput>;
  readonly update: UseMutationResult<
    AdminIdentifierTypeViewModel,
    Error,
    UpdateAdminIdentifierTypeInput,
    { previous: Snapshot }
  >;
  readonly remove: UseMutationResult<undefined, Error, string, { previous: Snapshot }>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: adminIdentifierTypesQueryKey }).catch(() => undefined);
  };

  const create = useMutation<AdminIdentifierTypeViewModel, Error, AdminIdentifierTypeInput>({
    mutationFn: async (input) =>
      mapAdminIdentifierType(await apiClient.createIdentifierDefinition(toDto(input))),
    onSettled: invalidate,
  });

  const update = useMutation<
    AdminIdentifierTypeViewModel,
    Error,
    UpdateAdminIdentifierTypeInput,
    { previous: Snapshot }
  >({
    mutationFn: async ({ id, ...rest }) =>
      mapAdminIdentifierType(await apiClient.updateIdentifierDefinition(id, toDto(rest, id))),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: adminIdentifierTypesQueryKey });
      const previous = queryClient.getQueryData<AdminIdentifierTypeViewModel[]>(
        adminIdentifierTypesQueryKey,
      );
      if (previous !== undefined) {
        const merged = previous.map((row) =>
          row.id === input.id
            ? {
                id: input.id,
                userEnteredId: input.userEnteredId,
                label: input.label,
                type: input.type,
                order: input.order,
                prefix: input.prefix,
                format: input.format,
                formatHint: input.formatHint,
                enforceFormat: input.enforceFormat,
                allowDuplicates: input.allowDuplicates,
                autoGenerate: input.autoGenerate,
                isPrimary: input.isPrimary,
                active: input.active,
              }
            : row,
        );
        const next = input.isPrimary ? applyPrimaryUniqueness(merged, input.id) : merged;
        queryClient.setQueryData<AdminIdentifierTypeViewModel[]>(
          adminIdentifierTypesQueryKey,
          next,
        );
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(adminIdentifierTypesQueryKey, context.previous);
      }
    },
    onSettled: invalidate,
  });

  const remove = useMutation<undefined, Error, string, { previous: Snapshot }>({
    mutationFn: (id) => apiClient.deleteIdentifierDefinition(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: adminIdentifierTypesQueryKey });
      const previous = queryClient.getQueryData<AdminIdentifierTypeViewModel[]>(
        adminIdentifierTypesQueryKey,
      );
      if (previous !== undefined) {
        queryClient.setQueryData<AdminIdentifierTypeViewModel[]>(
          adminIdentifierTypesQueryKey,
          previous.filter((row) => row.id !== id),
        );
      }
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(adminIdentifierTypesQueryKey, context.previous);
      }
    },
    onSettled: invalidate,
  });

  return { create, update, remove };
}
